import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import {
  deleteCookie,
  setCookie,
  buildLocalizedPath,
} from "../../common-service/CommonService";
import { StoreContext } from "../../context/StoreContext";
import { postRevokeToken, getUserSubscription } from "../../api/Api";
import StickyMenu from "../../../components/common/sticky-menu/StickyMenu.js";
import logo from "../../../assets/img/logo/logo-m.png";
import { useTranslation } from "react-i18next";
import useLangNavigate from "../../../components/common/useLangNavigate.js";
import { SUPPORTED_LOCALES, getLocaleLabel } from "../../../config/locales";
import profileIcon from "../../../assets/img/icons/profile-icon.png";
import "./Header.css";

const Header = () => {
  const {
    isLoggedIn,
    setIsLoggedIn,
    mobileMenu,
    setMobileMenu,
    user,
    setUser,
    subscriptionVersion,
  } = useContext(StoreContext);
  const sidebarRef = useRef(null);
  const navigate = useLangNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Remove language prefix for matching
  const basePath = location.pathname.split("/").slice(2).join("/");

  const [subscriptionStatus, setSubscriptionStatus] = useState("subscribed");

  // Only show locales that are fully routed + have a translation file.
  const languages = SUPPORTED_LOCALES.filter((l) => l.routed);

  const activeLang = useMemo(
    () =>
      languages.find((el) => el.code === i18n.language) ?? {
        code: "en",
        label: "English",
      },
    [i18n.language],
  );

  const onLanguageChange = useCallback(
    (lang_item) => (e) => {
      e.preventDefault();
      if (lang_item.code !== i18n.language) {
        const code = lang_item.code;
        i18n.changeLanguage(code);
        document.body.dir = i18n.dir();
        setCookie("lng", code);
        navigate("", code);
        window.location.reload(false);
      }
    },
    [i18n, navigate],
  );

  const handleOutsideClick = useCallback(
    (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMobileMenu(false);
      }
    },
    [setMobileMenu],
  );

  const handleLogout = () => {
    setMobileMenu(false);
    postRevokeToken()
      .then((res) => {
        deleteCookie("token");
        sessionStorage.removeItem("token");
        setIsLoggedIn(false);
        setUser({});
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ user_email_hash: undefined });
        navigate("/login");
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (isLoggedIn && user?.id) {
      const fetchStatus = async () => {
        try {
          const res = await getUserSubscription();
          const sub = res?.data;
          if (sub?.cancel_date && sub?.cancellation_reason === "payment_failed")
            setSubscriptionStatus("payment_failed");
          else if (sub?.cancel_date) setSubscriptionStatus("unsubscribed");
          else if (sub?.cancel_request)
            setSubscriptionStatus("pending_cancellation");
          else if (sub?.payment_failed_at) setSubscriptionStatus("payment_warning");
          else if (sub?.id) setSubscriptionStatus("subscribed");
          else setSubscriptionStatus("unsubscribed");
        } catch {
          setSubscriptionStatus("unsubscribed");
        }
      };
      fetchStatus();
    }
  }, [isLoggedIn, user, subscriptionVersion]);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    document.body.dir = i18n.dir();
    const cleanupStickyMenu = StickyMenu();
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      cleanupStickyMenu();
    };
  }, [handleOutsideClick, i18n]);

  useEffect(() => {
    const cookieLang = document.cookie
      .split("; ")
      .find((row) => row.startsWith("lng="))
      ?.split("=")[1];
    const supportedCodes = languages.map((lang) => lang.code);
    const rawLang = window.location.pathname.split("/")[1];
    const urlLang = supportedCodes.includes(rawLang) ? rawLang : "fr";
    const initialLang = supportedCodes.includes(urlLang)
      ? urlLang
      : cookieLang || "en";

    if (initialLang !== i18n.language) {
      i18n.changeLanguage(initialLang);
      setCookie("lng", initialLang);
      document.body.dir = i18n.dir(initialLang);
    }
  }, [i18n, languages]);

  return (
    <header className="header-primary aai-sticky">
      <div className="container" style={{ maxWidth: "95vw" }}>
        <nav
          ref={sidebarRef}
          className="navbar navbar-expand-xl justify-content-between"
        >
          {/* Left - Logo */}
          <div className="d-flex align-items-center">
            <img
              src={logo}
              alt="TaLegacy Logo"
              width="120"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/home")}
            />
          </div>

          {/* Center - Navigation */}
          <div
            className={`collapse navbar-collapse ${mobileMenu ? "show" : ""}`}
            id="navbarNav"
          >
            <ul className="navbar-nav mx-auto h-100">
              {isLoggedIn && (
                <>
                  <li className="nav-item">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("my-books") ||
                        basePath.startsWith("questions")
                          ? "active-tab"
                          : ""
                      }`}
                      to={buildLocalizedPath("/my-books")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("my_books")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("general-categories") &&
                        basePath.endsWith("/reformulate")
                          ? "active-tab"
                          : ""
                      }`}
                      to={buildLocalizedPath("/general-categories/1/reformulate")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("reformulation")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className={`nav-link ${
                        basePath === "" ||
                        basePath.startsWith("finished-book-options")
                          ? "active-tab"
                          : ""
                      }`}
                      to={buildLocalizedPath("/finished-book-options")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("write_a_new_book")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("printing") ? "active-tab" : ""
                      }`}
                      to={buildLocalizedPath("/printing")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("printing.title")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("audiobook-upload")
                          ? "active-tab"
                          : ""
                      }`}
                      to={buildLocalizedPath("/audiobook-upload")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("audiobook")}
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("gift-card") ? "active-tab" : ""
                      }`}
                      to={buildLocalizedPath("/gift-card")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("gift_card")}
                    </Link>
                  </li>
                </>
              )}

              {/* Mobile menu items for non-logged in users */}
              {!isLoggedIn && (
                <>
                  <li className="nav-item d-xl-none">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("printing") ? "active-tab" : ""
                      }`}
                      to={buildLocalizedPath("/printing")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("printing.title")}
                    </Link>
                  </li>
                  <li className="nav-item d-xl-none">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("gift-card") ? "active-tab" : ""
                      }`}
                      to={buildLocalizedPath("/gift-card")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("gift_card")}
                    </Link>
                  </li>
                  <li className="nav-item d-xl-none">
                    <Link
                      className="aai-btn btn-pill-solid"
                      style={{ borderRadius: "30px" }}
                      to={buildLocalizedPath("/register")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("signup")}
                    </Link>
                  </li>
                  <li className="nav-item d-xl-none">
                    <Link
                      className="aai-gradient-outline-btn me-4"
                      to={buildLocalizedPath("/login")}
                      onClick={() => setMobileMenu(false)}
                    >
                      {t("login")}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Right - Language / Profile / Auth */}
          <div className="navbar-right d-flex align-items-center gap-4">
            {/* Show printing + gift card in right section when not logged in (desktop only) */}
            <ul className="navbar-nav mx-auto h-100">
              {!isLoggedIn && (
                <>
                  <li className="nav-item list-unstyled d-none d-xl-block">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("printing") ? "active-tab" : ""
                      }`}
                      to={buildLocalizedPath("/printing")}
                    >
                      {t("printing.title")}
                    </Link>
                  </li>
                  <li className="nav-item list-unstyled d-none d-xl-block">
                    <Link
                      className={`nav-link ${
                        basePath.startsWith("gift-card") ? "active-tab" : ""
                      }`}
                      to={buildLocalizedPath("/gift-card")}
                    >
                      {t("gift_card")}
                    </Link>
                  </li>
                </>
              )}
            </ul>

            {/* Language Selector */}
            <div className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="d-inline-flex align-items-center gap-2">
                  <i className="bi bi-globe2" />
                  {activeLang?.code?.toUpperCase()}
                </span>
              </a>
              <ul className="dropdown-menu dropdown-menu-end">
                {languages
                  .filter((el) => el.code !== activeLang.code)
                  .map((lang, idx) => (
                    <li key={idx}>
                      <button
                        className="dropdown-item"
                        onClick={onLanguageChange(lang)}
                      >
                        {getLocaleLabel(lang.code)}
                      </button>
                    </li>
                  ))}
              </ul>
            </div>

            {/* Auth / Profile - Desktop only when not logged in */}
            {!isLoggedIn ? (
              <div className="align-items-center signup-in-links d-none d-xl-flex">
                <Link
                  to={buildLocalizedPath("/register")}
                  className="aai-btn btn-pill-solid signup"
                  style={{ borderRadius: "30px" }}
                >
                  {t("signup")}
                </Link>
                <Link
                  to={buildLocalizedPath("/login")}
                  className="aai-gradient-outline-btn me-4 login"
                >
                  {t("login")}
                </Link>
              </div>
            ) : (
              <>
                {subscriptionStatus === "unsubscribed" && (
                  <Link
                    to={buildLocalizedPath("/manage-subscriptions")}
                    className="aai-gradient-outline-btn d-none d-xl-block"
                    style={{ maxWidth: "200px" }}
                  >
                    {t("upgrade_account")}
                  </Link>
                )}

                <div className="nav-item dropdown">
                  <a
                    className="nav-link dropdown-toggle p-0"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span className="profile-icon-wrapper">
                      <img
                        src={profileIcon}
                        alt="Profile"
                        width={28}
                        height={28}
                        className="rounded-circle"
                      />
                      {subscriptionStatus === "payment_warning" && (
                        <span className="profile-payment-badge badge-warning">
                          <i className="bi bi-exclamation-triangle-fill" />
                        </span>
                      )}
                      {subscriptionStatus === "payment_failed" && (
                        <span className="profile-payment-badge badge-danger">
                          <i className="bi bi-exclamation-circle-fill" />
                        </span>
                      )}
                    </span>
                  </a>
                  <div className="dropdown-menu custom-left-dropdown">
                    <div className="dropdown-header">
                      {t("welcome")}, {user?.first_name || "User"} 👋
                    </div>
                    <Link
                      className="dropdown-item"
                      to={buildLocalizedPath("/profile")}
                    >
                      {t("my_profile")}
                    </Link>
                    {subscriptionStatus !== "unsubscribed" && subscriptionStatus !== "payment_failed" && (
                      <Link
                        className="dropdown-item"
                        to={buildLocalizedPath("/manage-subscriptions")}
                      >
                        {t("my_subscription")}
                      </Link>
                    )}
                    {subscriptionStatus !== "unsubscribed" && (
                      <Link
                        className="dropdown-item"
                        to={buildLocalizedPath("/payment-methods")}
                      >
                        <i className="bi bi-credit-card me-1" />
                        {t("payment_methods")}
                      </Link>
                    )}
                    {(subscriptionStatus === "payment_failed" || subscriptionStatus === "payment_warning") && (
                      <Link
                        className="dropdown-item text-danger"
                        to={buildLocalizedPath("/update-payment-method")}
                      >
                        <i className="bi bi-exclamation-circle me-1" />
                        {t("subscribe.update_payment_method")}
                      </Link>
                    )}
                    {subscriptionStatus === "unsubscribed" && (
                      <Link
                        className="dropdown-item d-xl-none"
                        to={buildLocalizedPath("/manage-subscriptions")}
                      >
                        <i className="bi bi-arrow-up-circle me-1" />
                        {t("upgrade_account")}
                      </Link>
                    )}
                    <button
                      className="dropdown-item text-danger"
                      onClick={handleLogout}
                    >
                      <i className="bi bi-box-arrow-right me-1" />
                      {t("logout")}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Mobile menu toggle */}
            <button
              className={`navbar-toggler d-block d-xl-none ${
                mobileMenu ? "collapsed" : ""
              }`}
              onClick={() => setMobileMenu(!mobileMenu)}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded={mobileMenu}
              aria-label="Toggle navigation"
            >
              <span></span>
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
